import {useEffect, useRef, useState} from 'react'
import utils from '@eitje/utils'
import {useModalContext} from './modal_context'
import {FormDeleteButton, FormSubmitButton} from './form/components/buttons'
import {DeleteButton, SubmitButton, BackButton, DocsButton} from '../eitje_button'
import {Layout} from '../layout'
import './styles/footer.less'
import {useLocation} from '@eitje/bridge'

export const ModalFooter = props => {
	const {pathname} = useLocation()
	const {leftFooterContent, hasFooter, hideCancel} = props
	const ns = 'eitje-modal-2-footer'

	// overwrite spaceBetween's default 100% width to ensure the content may overgrow the container,
	// otherwise the refs won't measure the fatty content
	const inlineStyles = {width: 'fit-content', minWidth: '100%'}
	const horizontalPadding = 16
	const footerRef = useRef()
	const contentRef = useRef()

	const [hasCancel, setHasCancel] = useState(!hideCancel)

	useEffect(() => {
		setHasCancel(!hideCancel)
	}, [hideCancel, pathname])

	useEffect(() => {
		if (!footerRef.current || !contentRef.current || hideCancel) return

		const checkSize = () => {
			if (!footerRef.current || !contentRef.current) return
			const footerWidth = footerRef.current.clientWidth
			const contentWidth = contentRef.current.clientWidth
			const footerContent = contentWidth + 2 * horizontalPadding
			const contentFits = footerContent <= footerWidth

			if (hasCancel && !contentFits) {
				setHasCancel(false)
			}
		}

		checkSize()

		const resizeObserver = new ResizeObserver(() => {
			checkSize()
		})

		resizeObserver.observe(footerRef.current)
		resizeObserver.observe(contentRef.current)

		return () => resizeObserver.disconnect()
	}, [hasCancel, hideCancel])

	if (!hasFooter) return null

	return (
		<Layout padding={`16 ${horizontalPadding}`} className={ns} width="full" ref={footerRef}>
			<Layout horizontal="spaceBetween" className={`${ns}-content`} style={inlineStyles} ref={contentRef}>
				{leftFooterContent}
				<Layout className={`${ns}-right`}>
					<RightFooter {...props} hasCancel={hasCancel} />
				</Layout>
			</Layout>
		</Layout>
	)
}

const RightFooter = props => {
	const {
		form,
		onSubmit,
		afterDel,
		submitIsDanger,
		SubmitButtonComponent,
		FooterSubmitButton,
		buttons,
		insertAtIndex,
		confirmSubmit,
		submitConfirmation,
		hasCancel,
		hideSubmit,
		onDel,
		collection,
		docsId,
		okButtonProps = {},
	} = props

	const getSubmitButton = () => {
		if (submitIsDanger) return FormDeleteButton
		if (SubmitButtonComponent) return SubmitButtonComponent
	}

	const {onCancel} = useModalContext()

	const ButtonComponent = getSubmitButton()
	let submitConfirmationProps
	if (!submitIsDanger) submitConfirmationProps = getSubmitConfirmation({submitConfirmation, confirmSubmit, okButtonProps})
	let formSubmitConfirmationProps = {}
	if (form && submitConfirmationProps) formSubmitConfirmationProps = {submitConfirmation: submitConfirmationProps}
	const SubmitButtonComponentLocal = FooterSubmitButton || (form ? FormSubmitButton : SubmitButton)
	const condOpts = form ? {} : {onClick: onSubmit}
	const showSubmit = !hideSubmit && (onSubmit || form)
	const backButton = <BackButton onClick={onCancel} />
	const showDocsButton = docsId || collection

	const _buttons = [
		hasCancel && backButton,
		onDel && <DeleteButton afterDel={afterDel} onDel={onDel} />,
		...utils.alwaysArray(buttons),
		showDocsButton && <DocsButton collection={collection} to={docsId} />,
		showSubmit && (
			<SubmitButtonComponentLocal
				colorSet="solid"
				ButtonComponent={ButtonComponent}
				{...okButtonProps}
				{...condOpts}
				{...formSubmitConfirmationProps}
			/>
		),
	].filter(Boolean)

	if (insertAtIndex) {
		Object.keys(insertAtIndex).forEach(idx => {
			const comp = insertAtIndex[idx]
			_buttons.splice(idx, 0, comp)
		})
	}

	return _buttons
}

const getSubmitConfirmation = ({submitConfirmation, confirmSubmit, okButtonProps}) => {
	if (submitConfirmation) return submitConfirmation
	if (!confirmSubmit) return null

	return {
		title: okButtonProps.confirmTitle,
		text: okButtonProps.confirmText,
		confirmButtonProps: okButtonProps.confirmButtonProps,
	}
}
